class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    const iconsearch = document.querySelector(".header__search");
    if (iconsearch)
      iconsearch.addEventListener("click", () => {
        this.classList.contains("active") && this.close();
      });

    const iconaccount = document.querySelector(".header__icon--account");
    if (iconaccount)
      iconaccount.addEventListener("click", () => {
        this.classList.contains("active") && this.close();
      });

    this.stickyHeaderAvailable = document.querySelector('sticky-header') ? true : false;

  }

  connectedCallback(){
    let stickyHeaderHeight = 0;
    document.querySelectorAll('.section-header').forEach(ele =>{
      stickyHeaderHeight += ele.getBoundingClientRect().height;
    });
    let headerHeight = stickyHeaderHeight;
    document.querySelectorAll('.utility-bar').forEach(ele =>{
      headerHeight += ele.getBoundingClientRect().height;
    });
    document.documentElement.style.setProperty('--header-announce-height', headerHeight+'px');
    document.documentElement.style.setProperty('--header-sticky-height', stickyHeaderHeight+'px');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains("active") ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden-drawer');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden-drawer');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  syncFilledState(hasItems) {
    this.classList.toggle('is-empty', !hasItems);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', !hasItems);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', !hasItems);
    const empty = this.querySelector('.drawer__inner-empty');
    if (empty) {
      if (hasItems) empty.setAttribute('hidden', '');
      else empty.removeAttribute('hidden');
    }
  }

  paintFromCart(cart) {
    const items = cart && cart.items ? cart.items : [];
    if (!items.length) {
      this.syncFilledState(false);
      return false;
    }

    const root = this.querySelector('#CartDrawer-CartItems') || this.querySelector('.js-contents');
    if (!root) return false;

    const rows = items
      .map((item, index) => {
        const title = escapeCartText(item.title || item.product_title || '');
        const href = safeCartUrl(item.url);
        const qty = Number(item.quantity) || 1;
        const linePrice = formatCartMoney(item.final_line_price, cart.currency);
        const image = item.image || (item.featured_image && item.featured_image.url) || '';
        const media = image
          ? `<td class="cart-item__media" role="cell"><a href="${href}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a><img class="cart-item__image" src="${safeCartUrl(
              image
            )}" alt="${title}" loading="lazy" width="150" height="150"></td>`
          : `<td class="cart-item__media" role="cell"></td>`;
        return `<tr id="CartDrawer-Item-${index + 1}" class="cart-item" role="row">${media}<td class="cart-item__details" role="cell"><a href="${href}" class="cart-item__name h4 break">${title}</a><div class="product-option">${qty} × ${formatCartMoney(
          item.final_price,
          cart.currency
        )}</div></td><td class="cart-item__totals" role="cell"><span class="price">${linePrice}</span></td></tr>`;
      })
      .join('');

    let wrapper = root.querySelector('.drawer__cart-items-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'drawer__cart-items-wrapper';
      const live = root.querySelector('#CartDrawer-LiveRegionText');
      if (live && live.parentNode === root) root.insertBefore(wrapper, live);
      else root.insertBefore(wrapper, root.firstChild);
    }
    wrapper.innerHTML = `<table class="cart-items" role="table"><tbody role="rowgroup">${rows}</tbody></table>`;

    const total = this.querySelector('.totals__total-value');
    if (total) total.textContent = formatCartMoney(cart.total_price, cart.currency);

    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = false;

    this.syncFilledState(true);
    return true;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.id];
      if (typeof html !== 'string' || !html) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const innerHTML = this.getSectionInnerHTML(html, section.selector);
      if (innerHTML == null) return;
      sectionElement.innerHTML = innerHTML;
    });

    this.syncFilledState(Boolean(this.querySelector('.cart-item')));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const node = parsed && parsed.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    if (window.AhriSystemCart && typeof window.AhriSystemCart.refreshCartUI === 'function') {
      window.AhriSystemCart.refreshCartUI({ open: false }).catch((e) => {
        console.error(e);
      });
      return;
    }
    const base = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
    fetch(`${base}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((sections) => this.applyRefreshedSections(sections))
      .catch((e) => {
        console.error(e);
      });
  }

  applyRefreshedSections(sections, options) {
    const source = sections && typeof sections === 'object' ? sections : {};
    const expected = Number(options && options.expectedItemCount) || 0;
    const liveItems = document.querySelectorAll('cart-drawer .cart-item');
    const liveCount = liveItems && liveItems.length ? liveItems.length : 0;
    const minNeeded = Math.max(expected, liveCount);

    const drawerHtml = source['cart-drawer'];
    if (typeof drawerHtml === 'string' && drawerHtml) {
      const incoming = countSectionCartItems(drawerHtml);
      if (!minNeeded || incoming >= minNeeded) {
        const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const sourceInner = parsed && parsed.querySelector('.drawer__inner');
        const targetInner = document.querySelector('cart-drawer .drawer__inner');
        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.syncFilledState === 'function') {
          const hasItems = Boolean(
            (parsed && parsed.querySelector('.cart-item')) || document.querySelector('cart-drawer .cart-item')
          );
          drawer.syncFilledState(hasItems);
        }
      }
    }

    const liveAfter = document.querySelectorAll('cart-drawer .cart-item');
    const liveAfterCount = liveAfter && liveAfter.length ? liveAfter.length : 0;
    // Badge HTML must not paint when the drawer is still empty of the expected lines.
    if (minNeeded && liveAfterCount < minNeeded) return;

    const bubbleInner = this.getSectionInnerHTML(source['cart-icon-bubble'], '.shopify-section');
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && bubbleInner != null) bubble.innerHTML = bubbleInner;
  }
}

function countSectionCartItems(html) {
  const source = String(html || '');
  const byId = (source.match(/id=["']CartDrawer-Item-\d+["']/g) || []).length;
  if (byId) return byId;
  return (source.match(/class=["'][^"']*\bcart-item\b[^"']*["']/g) || []).length;
}

function escapeCartText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function safeCartUrl(value) {
  const url = String(value || '');
  if (!url || /[<>"']/.test(url)) return '#';
  if (url.charAt(0) === '/' && url.charAt(1) !== '/') return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (/^\/\/[a-z0-9.-]+/i.test(url)) return url;
  return '#';
}

function formatCartMoney(cents, currency) {
  const amount = (Number(cents) || 0) / 100;
  const code = String(currency || 'USD').toUpperCase();
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency: code });
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
